import { useMemo } from 'react';
import useListPipelines from '../../admin/pipelines/hooks/useListPipelines';
import {
  useAverageUsedCPUByNamespace,
  useAverageReqCPUByNamespace,
  useAverageUsedGPUByNamespace,
  useAverageReqGPUByNamespace,
  useAverageUsedMemByNamespace,
  useAverageReqMemByNamespace,
  useTotalLogStorageByTopicName,
} from './useConsumptionMetricsForPipeline';

const extractNamespaceValue = (metricsArray, namespace) => {
  if (!metricsArray?.length) {
    return { value: null };
  }
  return metricsArray.find(({ namespace: thisNamespace }) => namespace === thisNamespace);
}

const extractTopicValue = (metricsArray, topic) => {
  if (!metricsArray?.length) {
    return { value: null };
  }
  return metricsArray.find(({ topic: thisTopic }) => thisTopic === topic);
}

const isNullOrUndefined = (val) => val === null || val === undefined;

export const usePipelineListWithAggregatedMetrics = ({
 startTimestamp, endTimestamp,
}) => {
  const {
    data: pipelines,
    isLoading,
    error,
  } = useListPipelines(true);

  const { data: usedCpuData, isLoading: cpuUsedLoading, error: cpuUsedError } = useAverageUsedCPUByNamespace({ startTimestamp, endTimestamp });
  const { data: reqCpuData, isLoading: cpuReqLoading, error: cpuReqError } = useAverageReqCPUByNamespace({ startTimestamp, endTimestamp });
  const { data: usedMemData, isLoading: memUsedLoading, error: memUsedError } = useAverageUsedMemByNamespace({ startTimestamp, endTimestamp });
  const { data: reqMemData, isLoading: reqMemLoading, error: memReqError } = useAverageReqMemByNamespace({ startTimestamp, endTimestamp });
  const { data: usedGpuData, isLoading: gpuUsedLoading, error: gpuUsedError } = useAverageUsedGPUByNamespace({ startTimestamp, endTimestamp });
  const { data: reqGpuData, isLoading: gpuReqLoading, error: gpuReqError } = useAverageReqGPUByNamespace({ startTimestamp, endTimestamp });
  const { data: logStorageData, isLoading: logStorageLoading } = useTotalLogStorageByTopicName({ startTimestamp, endTimestamp });

  const pipelinesWithMetrics = useMemo(() => {
    if (!pipelines?.length) {
      return [];
    }
    return pipelines.map((pipeline) => {
      const namespace = `${pipeline?.pipeline?.name}-${pipeline?.deployment?.id}`;
      const topicName = pipeline?.plateau_topic;
      const cpuUsedRaw = extractNamespaceValue(usedCpuData, namespace);
      const cpuReqRaw = extractNamespaceValue(reqCpuData, namespace);
      const memUsedRaw = extractNamespaceValue(usedMemData, namespace);
      const memReqRaw = extractNamespaceValue(reqMemData, namespace);
      const gpuUsedRaw = extractNamespaceValue(usedGpuData, namespace);
      const gpuReqRaw = extractNamespaceValue(reqGpuData, namespace);
      const logStorageRaw = extractTopicValue(logStorageData, topicName)

      const cpuUsed = isNullOrUndefined(cpuUsedRaw?.value) ? null : Number(cpuUsedRaw.value)
      const cpuReq = isNullOrUndefined(cpuReqRaw?.value) ? null : Number(cpuReqRaw.value)
      const memUsed = isNullOrUndefined(memUsedRaw?.value) ? null : Number(memUsedRaw.value)
      const memReq = isNullOrUndefined(memReqRaw?.value) ? null : Number(memReqRaw.value)
      const gpuUsed = isNullOrUndefined(gpuUsedRaw?.value) ? null : Number(gpuUsedRaw.value)
      const gpuReq = isNullOrUndefined(gpuReqRaw?.value) ? null : Number(gpuReqRaw.value)
      const logSize = isNullOrUndefined(logStorageRaw?.value) ? null : Number(logStorageRaw.value)

      return ({
        ...pipeline,
        metrics: {
          cpuUsed, cpuReq, memUsed, memReq, gpuUsed, gpuReq,
          cpuPercent: cpuReq && cpuUsed ? cpuUsed / cpuReq : null,
          memPercent: memReq && memUsed ? memUsed / memReq : null,
          gpuPercent: gpuReq && gpuUsed ? gpuUsed / gpuReq : null,
          logSize,
        }
      })
    })
  }, [pipelines, reqCpuData, reqGpuData, reqMemData, usedCpuData, usedGpuData, usedMemData, logStorageData])

  const errorMessage = useMemo(() => {
    if (error) {
      return 'An unexpected error occurred while trying to load the pipeline list.'
    }
    if (cpuReqError || cpuUsedError || gpuUsedError || gpuReqError || memUsedError || memReqError){
      return 'The selected time frame is too large to render values.  Please try again with a smaller range.'
    }
    return null;
  }, [cpuReqError, cpuUsedError, error, gpuReqError, gpuUsedError, memReqError, memUsedError])

  return {
    pipelinesWithMetrics,
    isLoading: isLoading || cpuReqLoading || cpuUsedLoading || memUsedLoading || reqMemLoading || gpuReqLoading || gpuUsedLoading || logStorageLoading,
    error: errorMessage,
  }
}

export default usePipelineListWithAggregatedMetrics;